import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/isSpoofed'
import isInsuranceSupported, { isInsuranceSupportedAlongsideBookingProtection } from 'checkout/selectors/request/isInsuranceSupported'
import isPassportCardInsuranceSupportedSpoofed from 'checkout/selectors/request/isPassportCardInsuranceSupportedSpoofed'
import { selectNewInsuranceUiIsVariant } from 'lib/insurance/newInsuranceUiExperiment'
import isBookingProtectionEnabled, { isBookingProtectionEnabledIncludingFijiAndMaldives } from 'checkout/selectors/request/isBookingProtectionEnabled'
import { isSideBySideInsuranceCheckout } from 'checkout/selectors/request/sideBySideInsuranceContext'
import hasBookingProtectionInsuranceFetchError from 'checkout/selectors/request/hasBookingProtectionInsuranceFetchError'
import { isStandaloneFlightCfmrRegion } from 'checkout/selectors/request/isBookingProtectionAvailable'
import { checkoutContainsFlightsWithEmbeddedInsurance, getFlightItems, isStandaloneFlights } from 'checkout/selectors/view/flights'
import { getBookingProtectionOccupantsFromCartItems } from 'checkout/selectors/request/bookingProtectionOccupants'
import isInsuranceBookingProtectionPostPurchaseEnabled from 'selectors/isInsuranceBookingProtectionPostPurchaseEnabled'
import { Experiments } from 'constants/experiments'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import {
  findPostPurchaseCheckout,
  isBedbankItem,
  isBookingProtectionItem,
  isFlightItem,
  isInsuranceItem,
  isLuxPlusSubscriptionItem,
  isSubscriptionJoinItem,
} from 'lib/checkout/checkoutUtils'
import isGroupPaymentShareCheckout from 'checkout/selectors/payment/groupPayment/isGroupPaymentShareCheckout'
import { isFlightWithEmbeddedInsurance } from 'checkout/utils'
import { LE_LIVE_UTM_MEDIUM } from 'constants/payment'
import getIsLoyaltyEsimUpsellPostPurchaseCheckout from 'checkout/selectors/loyalty/esim/getIsLoyaltyEsimUpsellPostPurchaseCheckout'

/**
 * Whether booking protection (CFMR) can be offered for this checkout on business rules alone:
 * eligible cart items, region, occupancy and the flex-package rules. It does not consider
 * whether the CFMR product list or quote could be fetched, so consumers that fall back to
 * another product on a CFMR outage can keep their own section.
 */
function createIsBookingProtectionAvailableForCheckout(enabledSelector: (state: App.State) => boolean) {
  return createSelector(
    (state: App.State) => enabledSelector(state),
    (state: App.State) => getBookingProtectionOccupantsFromCartItems(state),
    (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
    (state: App.State) => isInsuranceBookingProtectionPostPurchaseEnabled(state),
    (state: App.State) => state.checkout.cart.items,
    (state: App.State) => getOptimizelyExperimentVariation(state, Experiments.croFlexCfmrBedbankPackages.id),
    (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
    (state: App.State) => getFlightItems(state),
    (state: App.State) => getIsLoyaltyEsimUpsellPostPurchaseCheckout(state),
    isGroupPaymentShareCheckout,
    (
      enabledForCartItems,
      bookingProtectionOccupants,
      postPurchase,
      isPostPurchaseEnabled,
      checkoutItems,
      isCroFlexCfmrBedbankPackagesEnabled,
      hasProtectedFlights,
      flights,
      isLoyaltyEsimUpsellPostPurchaseCheckout,
      isGroupPaymentShare,
    ): boolean => {
      if (isLoyaltyEsimUpsellPostPurchaseCheckout) return false

      // Paying a group share is payment-only — never offer booking protection add-ons.
      if (isGroupPaymentShare) {
        return false
      }

      if (postPurchase) {
        return isPostPurchaseEnabled
      }

      if (!bookingProtectionOccupants) return false

      if (!isCroFlexCfmrBedbankPackagesEnabled) return enabledForCartItems

      const hasBedbankAndFlexPackageIsEnabled = checkoutItems.some(item => isBedbankItem(item)) && !!isCroFlexCfmrBedbankPackagesEnabled

      const isStandaloneBedbankCheckout = checkoutItems.length > 0
        && checkoutItems.every(item => isBedbankItem(item) || isBookingProtectionItem(item) || isInsuranceItem(item) || isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item))
        && checkoutItems.some(item => isBedbankItem(item))

      if (isStandaloneBedbankCheckout && hasBedbankAndFlexPackageIsEnabled) {
        return false
      }

      const containsOnlyBedbankAndFlights = checkoutItems.every(item => isBedbankItem(item) || isInsuranceItem(item) || isLuxPlusSubscriptionItem(item) || isFlightItem(item))

      if (hasProtectedFlights && hasBedbankAndFlexPackageIsEnabled && containsOnlyBedbankAndFlights) {
        const flightItem = flights[0]
        const securedFlights = flightItem?.flights.filter(isFlightWithEmbeddedInsurance)

        if (securedFlights.length === flightItem.flights.length) {
          return false
        }
      }

      return enabledForCartItems
    },
  )

  /**
   * Whether the CFMR section should render: available on business rules and its product list and
   * quote loaded without error.
   */
}

export const isBookingProtectionAvailableForCheckout = createIsBookingProtectionAvailableForCheckout(isBookingProtectionEnabled)

/**
 * Cancellation protection availability with the Maldives and Fiji exclusions lifted, so both cohorts
 * of the side-by-side insurance experiment are judged on the same audience.
 */
const isBookingProtectionAvailableForSideBySideInsurance = createIsBookingProtectionAvailableForCheckout(isBookingProtectionEnabledIncludingFijiAndMaldives)

export const checkoutShouldShowBookingProtection = createSelector(
  (state: App.State) => isBookingProtectionAvailableForCheckout(state),
  (state: App.State) => hasBookingProtectionInsuranceFetchError(state),
  (isAvailable, hasInsuranceFetchError): boolean => isAvailable && !hasInsuranceFetchError,
)

export function isLeLiveUtmMedium(utmMedium: string | undefined): boolean {
  return utmMedium?.toLowerCase() === LE_LIVE_UTM_MEDIUM
}

export function shouldShowSpoofedInsuranceSectionForInputs(
  isSpoofed: boolean,
  insuranceSupported: boolean,
  isBookingProtectionSupported: boolean,
  passportCardInsuranceSupported: boolean,
  utmMedium: string | undefined,
): boolean {
  if (!isSpoofed) return false

  if (isLeLiveUtmMedium(utmMedium)) {
    return passportCardInsuranceSupported
  }

  return insuranceSupported || isBookingProtectionSupported || passportCardInsuranceSupported
}

export const getShouldShowSpoofedInsuranceSection = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isInsuranceSupported(state),
  (state: App.State) => checkoutShouldShowBookingProtection(state),
  (state: App.State) => isPassportCardInsuranceSupportedSpoofed(state),
  (state: App.State) => state.utm.medium,
  (isSpoofed, insuranceSupported, isBookingProtectionSupported, passportCardInsuranceSupported, utmMedium): boolean => {
    return shouldShowSpoofedInsuranceSectionForInputs(
      isSpoofed,
      insuranceSupported,
      isBookingProtectionSupported,
      passportCardInsuranceSupported,
      utmMedium,
    )
  },
)

export const getShouldShowRegularInsuranceSection = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isInsuranceSupported(state),
  (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
  (state: App.State) => checkoutShouldShowBookingProtection(state),
  (state: App.State) => isStandaloneFlights(state),
  (
    isSpoofed,
    insuranceSupported,
    checkoutContainsFlightsWithEmbeddedInsurance,
    isBookingProtectionSupported,
    isStandaloneFlights,
  ): boolean => {
    return !isSpoofed &&
      insuranceSupported &&
      !checkoutContainsFlightsWithEmbeddedInsurance &&
      (!isBookingProtectionSupported || isStandaloneFlights)
  },
)

export const getShouldShowBookingProtectionSection = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => checkoutShouldShowBookingProtection(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
  (state: App.State) => isStandaloneFlightCfmrRegion(state),
  (
    isSpoofed,
    isBookingProtectionSupported,
    isStandaloneFlights,
    checkoutContainsFlightsWithEmbeddedInsurance,
    standaloneFlightCfmrRegion,
  ): boolean => {
    return !isSpoofed &&
      isBookingProtectionSupported &&
      (!isStandaloneFlights || standaloneFlightCfmrRegion) &&
      (!checkoutContainsFlightsWithEmbeddedInsurance ||
        (!isStandaloneFlights && checkoutContainsFlightsWithEmbeddedInsurance))
  },
)

/**
 * Whether this checkout presents CFMR and XCover travel protection together: both products are
 * offered to a fresh, non-spoofed customer decision, both funnel toggles are on and PassportCard
 * is not replacing the insurance options. This is the single decision behind the section list,
 * the section title and the experiment activation, so every cohort activates on the same
 * audience and a treatment customer is never shown the control section.
 *
 * CFMR availability is judged on business rules only: when its product list or quote fails to
 * load, the section still renders and hides that card, because the regular insurance section
 * does not offer travel protection while CFMR is enabled.
 */
export const getIsSideBySideInsuranceEligible = createSelector(
  (state: App.State) => isSideBySideInsuranceCheckout(state),
  (state: App.State) => isBookingProtectionAvailableForSideBySideInsurance(state),
  (state: App.State) => isInsuranceSupportedAlongsideBookingProtection(state),
  (isSideBySideCheckout, isBookingProtectionAvailable, isTravelInsuranceSupported): boolean =>
    isSideBySideCheckout && isBookingProtectionAvailable && isTravelInsuranceSupported,
)

export const getShouldShowSideBySideInsuranceSection = createSelector(
  (state: App.State) => getIsSideBySideInsuranceEligible(state),
  (state: App.State) => selectNewInsuranceUiIsVariant(state),
  (isEligible, isVariant): boolean => isEligible && isVariant,
)
